import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, MessagesSquare } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchUserGroups, type AdminUserGroup } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A3.12 — Foydalanuvchi a'zo bo'lgan guruh/kanallar. GET /admin/users/:id/groups
 * (backend `f5fe3cb`). Har chat → guruh kartasiga havola (cross-link).
 */
export function UserGroups() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminUserGroup>[]>(
    () => [
      {
        key: 'title',
        header: t.col.title,
        type: 'text',
        minWidth: 200,
        sortable: false,
        render: (r) => (
          <Link to={`/groups/${r.id}`} className="text-accent hover:underline">
            {r.title || (r.username ? `@${r.username}` : `#${r.id}`)}
          </Link>
        ),
      },
      {
        key: 'type',
        header: t.col.type,
        type: 'enum',
        width: 110,
        options: [
          { value: 'group', label: t.groups.group, status: 'active' },
          { value: 'channel', label: t.groups.channel, status: 'review' },
        ],
      },
      {
        key: 'role',
        header: t.groups.admin,
        type: 'enum',
        width: 120,
        options: [
          { value: 'owner', label: t.groups.owner, status: 'banned' },
          { value: 'admin', label: t.groups.admin, status: 'review' },
          { value: 'member', label: t.groups.member, status: 'active' },
        ],
      },
      {
        key: 'member_count',
        header: t.groups.members,
        type: 'text',
        width: 110,
        render: (r) => <span className="font-mono tabular">{r.member_count.toLocaleString()}</span>,
      },
      { key: 'joined_at', header: t.groups.joined, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchUserGroups(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.users.groups}
        screenId="A3.12"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminUserGroup>
        screenId="A3.12"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <MessagesSquare size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
